import { supabase } from '../../lib/supabaseClient';
import { useAuthStore } from '../../store/useAuthStore';
import type { Database } from '../../types/database.types';

export type TaskTemplate = Database['public']['Tables']['task_templates']['Row'];

export type TaskTemplateInput = {
  content: string;
  description?: string | null;
  priority?: number;
  tag_ids?: string[];
  estimated_duration_minutes?: number | null;
  is_active?: boolean;
  /** Couleur des tâches créées depuis cette tâche type. null : couleur neutre. */
  color?: string | null;
};

function currentCollaboratorId(): string | null {
  return useAuthStore.getState().collaborator?.id ?? null;
}

export async function listTaskTemplates(activeOnly: boolean): Promise<TaskTemplate[]> {
  let q = supabase
    .from('task_templates')
    .select('*')
    .order('display_order')
    .order('created_at');
  if (activeOnly) q = q.eq('is_active', true);
  const { data, error } = await q;
  if (error) throw new Error(`Impossible de charger les tâches types : ${error.message}`);
  return data ?? [];
}

export async function createTaskTemplate(input: TaskTemplateInput): Promise<TaskTemplate> {
  const content = input.content.trim();
  if (!content) throw new Error('Le titre de la tâche type est obligatoire');

  const { data, error } = await supabase
    .from('task_templates')
    .insert({
      content,
      description: input.description?.trim() || null,
      priority: input.priority ?? 3,
      tag_ids: input.tag_ids ?? [],
      estimated_duration_minutes: input.estimated_duration_minutes ?? null,
      is_active: input.is_active ?? true,
      created_by: currentCollaboratorId(),
    })
    .select()
    .single();
  if (error) throw new Error(`Impossible de créer la tâche type : ${error.message}`);
  return data;
}

export async function updateTaskTemplate(
  id: string,
  patch: Partial<TaskTemplateInput>,
): Promise<TaskTemplate> {
  const payload: Database['public']['Tables']['task_templates']['Update'] = {
    updated_at: new Date().toISOString(),
  };
  if (patch.content !== undefined) payload.content = patch.content.trim();
  if (patch.description !== undefined) payload.description = patch.description?.trim() || null;
  if (patch.priority !== undefined) payload.priority = patch.priority;
  if (patch.tag_ids !== undefined) payload.tag_ids = patch.tag_ids;
  if (patch.estimated_duration_minutes !== undefined) {
    payload.estimated_duration_minutes = patch.estimated_duration_minutes;
  }
  if (patch.is_active !== undefined) payload.is_active = patch.is_active;
  if (patch.color !== undefined) payload.color = patch.color;

  const { data, error } = await supabase
    .from('task_templates')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(`Impossible de modifier la tâche type : ${error.message}`);
  return data;
}

export async function deleteTaskTemplate(id: string): Promise<void> {
  const { error } = await supabase.from('task_templates').delete().eq('id', id);
  if (error) throw new Error(`Impossible de supprimer la tâche type : ${error.message}`);
}
